import { useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { useGetApiProjectsQuery } from '../../app/athenaApiGen';
import { setPage, setPageSize, setSearch, setProgramId, setOwnerId } from '../../app/projects/projectsSlice';
import { ADMIN_SITEWIDE_ACCESS } from './aclActions';

export const useProjectsList = () => {
  const dispatch = useDispatch();
  const { page, pageSize, search, programId, ownerId } = useSelector(
    (state: any) => ({
      page: state.projects.page,
      pageSize: state.projects.pageSize,
      search: state.projects.search,
      programId: state.projects.programId,
      ownerId: state.projects.ownerId,
    }),
    shallowEqual
  );
  const { actions, loggedIn, actionsLoaded } = useSelector((state: any) => state.user);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Check if user has admin access
  const isAdmin = useMemo(() => {
    if (!actions || actions.length === 0) return false;
    return actions.some((action: any) => action?.action === ADMIN_SITEWIDE_ACCESS);
  }, [actions]);

  // Check if user has list-projects permission (following Deliverables pattern)
  // The backend will auto-filter results based on JWT claims
  const canListProjects = useMemo(() => {
    if (!actions || actions.length === 0) return false;
    return actions.some((action: any) => action?.action === 'list-projects');
  }, [actions]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch projects with search and filter parameters
  // Following Deliverables pattern: DON'T pass programId from JWT
  // Only pass programId when user explicitly filters via UI dropdown
  // Backend auto-filters based on JWT claims
  const { data, isLoading, isFetching, isError, refetch } = useGetApiProjectsQuery({
    page: page + 1, // API uses 1-based indexing
    pageSize: pageSize,
    includeArchived: false,
    search: debouncedSearch || undefined,
    // Only pass programId if user explicitly selected a filter in UI
    programId: programId || undefined,
    ownerId: ownerId || undefined,
  }, {
    // Skip if not logged in OR if actions not yet loaded OR if user doesn't have list-projects permission
    skip: !loggedIn || !actionsLoaded || !canListProjects,
  });

  const handleSearchChange = useCallback((value: string) => {
    dispatch(setSearch(value));
  }, [dispatch]);

  const handleProgramIdChange = useCallback((value: string) => {
    dispatch(setProgramId(value));
  }, [dispatch]);

  const handleOwnerIdChange = useCallback((value: string) => {
    dispatch(setOwnerId(value));
  }, [dispatch]);

  const handlePageChange = useCallback((newPage: number) => {
    dispatch(setPage(newPage));
  }, [dispatch]);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    dispatch(setPageSize(newPageSize));
  }, [dispatch]);

  return {
    projects: data?.projects || [],
    totalCount: data?.total || 0,
    loading: isLoading || isFetching,
    isError,
    refetch,
    page,
    pageSize,
    search,
    debouncedSearch,
    programId,
    ownerId,
    // ACL-related info
    isAdmin,
    canListProjects,
    showNoPermissions: !canListProjects,
    handleSearchChange,
    handleProgramIdChange,
    handleOwnerIdChange,
    handlePageChange,
    handlePageSizeChange,
  };
};
